<template>
  <div class="pdf-panel">
    <!-- 加载 / 失败态（PDF 正文未就绪或渲染失败） -->
    <div v-if="loadState !== 'ready'" class="pdf-state">
      <template v-if="loadState === 'error'">
        <div class="pdf-state-text">PDF 渲染失败</div>
        <a-button size="small" @click="downloadFile">下载原文件</a-button>
      </template>
      <template v-else>
        <LoadingOutlined class="pdf-state-spin" />
        <div class="pdf-state-text">合同文件加载中…</div>
      </template>
    </div>

    <!-- PDF 正文（vue-pdf-embed 渲染全部页，容器内滚动） -->
    <div v-show="loadState === 'ready'" ref="pdfWrapRef" class="pdf-scroll">
      <VuePdfEmbed
        v-if="pdfWidth > 0"
        :source="pdfSource"
        :width="pdfWidth"
        @rendered="retryCount = 0"
        @rendering-failed="handleRenderingFailed"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, watch, nextTick, onMounted, onBeforeUnmount } from "vue";
import { LoadingOutlined } from "@ant-design/icons-vue";
import VuePdfEmbed from "vue-pdf-embed";

const props = defineProps({
  // 合同二进制（getFile 返回的 PDF Blob）
  documentBuffer: { type: [Blob, ArrayBuffer, Uint8Array], default: null },
  contractName: { type: String, default: "合同" },
});

/* loadState：idle → loading → ready | error */
const loadState = ref("idle");
const pdfSource = ref(null);
const pdfWidth = ref(0);
const pdfWrapRef = ref(null);

const load = async (buf) => {
  if (!buf) return;
  loadState.value = "loading";
  try {
    // source 由 vue-pdf-embed 直接透传给 pdf.js getDocument（DocumentInitParameters）：
    // - data 接受 Uint8Array（Blob 先转 ArrayBuffer）
    // - cMapUrl / standardFontDataUrl：非嵌入字体（常见于中文 PDF）缺这两项时
    //   字形加载失败，文字消失
    // - wasmUrl：JBIG2/JPEG2000 图像解码器。扫描件常用 JBIG2 压缩，缺它图像
    //   解码全部失败，页面只剩表格线等矢量内容
    // 注 1：三项 URL 齐备后 pdf.js 走 useWorkerFetch 快路径（worker 直接取资源）
    // 注 2：必须用带 origin 的绝对 URL——worker 可能运行在 blob: 上下文，
    //       根路径 "/wasm/..." 在其中无法解析（fetch/import 直接抛错且不留
    //       网络请求）；绝对 URL 在任意基准下都可解析
    const ab = buf instanceof Blob ? await buf.arrayBuffer() : buf;
    const assetBase = new URL(process.env.BASE_URL, window.location.origin).href;
    pdfSource.value = {
      data: new Uint8Array(ab),
      cMapUrl: `${assetBase}cmaps/`,
      cMapPacked: true,
      standardFontDataUrl: `${assetBase}standard_fonts/`,
      wasmUrl: `${assetBase}wasm/`,
    };
    loadState.value = "ready";
  } catch (err) {
    console.error("[PdfPanel] load pdf error:", err);
    loadState.value = "error";
  }
};

watch(
  () => props.documentBuffer,
  (buf) => load(buf),
  { immediate: true },
);

/* 宽度自适应：按滚动容器内宽渲染（留 24px 边距）。
   resize 时防抖后再改渲染宽度：vue-pdf-embed 对 width 变化的处理是
   「中止旧渲染 → 整体重渲」，但连续快速变宽时其内部中止逻辑存在竞态，
   多轮渲染会并发调用 pdf.js 的 page.render 写同一批 canvas，
   抛 "Cannot use the same canvas during multiple render() operations"
   → rendering-failed（表现为视口一变 PDF 就消失）。防抖让宽度变化
   成为彼此独立的单次重渲染，规避并发路径 */
let resizeObserver = null;
let widthTimer = null;
let retryTimer = null;
let retryCount = 0;

const applyWidth = () => {
  const el = pdfWrapRef.value;
  if (el && el.clientWidth > 0) {
    pdfWidth.value = Math.floor(el.clientWidth - 24);
  }
};
const updateWidth = () => {
  // 首次（pdfWidth 尚为 0）立即应用，避免首屏渲染被防抖拖延
  if (pdfWidth.value === 0) {
    applyWidth();
    return;
  }
  if (widthTimer) clearTimeout(widthTimer);
  widthTimer = setTimeout(applyWidth, 150);
};

/* 渲染失败兜底：失败大多是大文档慢渲染下残留的并发竞态，
   而非文件本身问题。300ms 后微调宽度触发一次干净的全新渲染，
   连续失败超过 2 次才落到错误态；rendered 成功即重置计数 */
const handleRenderingFailed = (err) => {
  console.error("[PdfPanel] render pdf failed:", err);
  if (retryTimer) clearTimeout(retryTimer);
  if (retryCount >= 2) {
    loadState.value = "error";
    return;
  }
  retryCount += 1;
  const target = pdfWidth.value;
  retryTimer = setTimeout(() => {
    // 先偏移 1px 触发重渲，再恢复目标宽度（各自是独立的单次变更）
    pdfWidth.value = Math.max(1, target - 1);
    retryTimer = setTimeout(() => {
      if (pdfWidth.value !== target) pdfWidth.value = target;
    }, 60);
  }, 300);
};

watch(loadState, async (s) => {
  if (s === "ready") {
    await nextTick();
    applyWidth();
  }
});
onMounted(() => {
  applyWidth();
  if (window.ResizeObserver && pdfWrapRef.value) {
    resizeObserver = new ResizeObserver(updateWidth);
    resizeObserver.observe(pdfWrapRef.value);
  }
});
onBeforeUnmount(() => {
  if (resizeObserver) resizeObserver.disconnect();
  if (widthTimer) clearTimeout(widthTimer);
  if (retryTimer) clearTimeout(retryTimer);
});

/* 下载原始 PDF（结果页头部「下载文件」按钮调用） */
const downloadFile = () => {
  const buf = props.documentBuffer;
  if (!buf) return;
  const blob =
    buf instanceof Blob ? buf : new Blob([buf], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = /\.pdf$/i.test(props.contractName)
    ? props.contractName
    : `${props.contractName || "合同"}.pdf`;
  a.click();
  URL.revokeObjectURL(url);
};
defineExpose({ downloadFile });
</script>

<style lang="less" scoped>
.pdf-panel {
  height: 100%;
  background: #f3f6fb;
  display: flex;
  flex-direction: column;
  min-height: 0;
}
.pdf-state {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: #64748f;
}
.pdf-state-spin {
  font-size: 22px;
  color: #1768ff;
  animation: pdf-spin 1s linear infinite;
}
@keyframes pdf-spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
.pdf-state-text {
  font-size: 14px;
}
.pdf-scroll {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 16px 12px;
  display: flex;
  justify-content: center;
}
.pdf-scroll :deep(.vue-pdf-embed) {
  width: 100%;
}
</style>
